/**
 * AddUsers.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Dynamic Add / Edit User form built with MUI.
 *
 * Props:
 *   userData  {object|null} — null → Add mode; object → Edit mode
 *   onSave    {function}    — called with validated form data
 *   onDelete  {function}    — called with user id (Edit mode only)
 *   onClose   {function}    — called to dismiss the dialog
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
  Typography,
  CircularProgress
} from '@mui/material';
import { Save, Trash } from 'lucide-react';
import { AuthConstant, RoleOptions } from '../../../../constants/constant';
import { FilterFieldSx, DropDownSX, CSS_PRIMARY } from '../../../../constants/style';
import { saveUser, deleteUser } from '../../../../apis/Masters/Users';
import { isDeleteAllowed, isReadonlyMode } from '../../../../utils';
import { MastersId } from '../../../../constants/constant';
import { fetchGlobalTypesCombo } from '../../../../apis/Masters/GlobalType';

/* ── Empty form state ─────────────────────────────────────────────────────── */
const emptyForm = {
  userName: '',
  password: '',
  confirmPassword: '',
  roleGUID: null,
  isActive: true
};

/* ── Validation ───────────────────────────────────────────────────────────── */
const validate = (form, isEditMode) => {
  const errors = {};

  if (!form.userName.trim()) errors.userName = 'Username is required.';

  if (!isEditMode) {
    if (!form.password) errors.password = 'Password is required.';

    if (!form.confirmPassword) errors.confirmPassword = 'Please confirm your password.';
    else if (form.confirmPassword !== form.password)
      errors.confirmPassword = 'Passwords do not match.';
  }

  if (!form.roleGUID) errors.roleGUID = 'Role is required.';

  return errors;
};

/* ════════════════════════════════════════════════════════════════════════════
   AddUsers Component
   ════════════════════════════════════════════════════════════════════════════ */
const AddUsers = ({ userData = null, userAccess = null, onRefresh, onClose }) => {
  const isEditMode = Boolean(userData);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const isDeleteAllowd = isEditMode ? isDeleteAllowed() : false;
  const [deleting, setDeleting] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const isEditAllowed = userAccess?.hasEditPermission || userAccess?.hasAddPermission;

  // ── Prefill on Edit mode ───────────────────────────────────────────────────
  useEffect(() => {
    if (userData) {
      setForm({
        userGUID: userData.userGUID || 0,
        userName: userData.userName || userData.username || '',
        password: userData.password || '',
        confirmPassword: userData.password || '',
        roleGUID: userData.roleGUID || '',
        isActive: userData.isActive !== undefined ? userData.isActive : true
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
    fetchDependencies();
  }, [userData]);

  // ── Field change ───────────────────────────────────────────────────────────
  const handleChange = (field) => (e) => {
    const value =
      e.target.type === 'checkbox'
        ? e.target.checked
        : !isNaN(e.target.value)
          ? e.target.value
          : e.target.value || '';
    const next = { ...form, [field]: value };
    setForm(next);
    // Live-validate touched fields
    if (touched[field]) {
      const errs = validate(next, isEditMode);
      setErrors((prev) => ({ ...prev, [field]: errs[field] }));
    }
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validate(form, isEditMode);
    setErrors((prev) => ({ ...prev, [field]: errs[field] }));
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    // Touch all fields to reveal errors
    const allTouched = Object.fromEntries(Object.keys(form).map((k) => [k, true]));
    setTouched(allTouched);

    const errs = validate(form, isEditMode);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    const payload = {
      userGUID: form.userGUID,
      userName: (form.userName.trim()).toUpperCase(),
      password: form.password,
      roleGUID: form.roleGUID,
      isActive: form.isActive,
      versionNo: '',
      roleName: '',
      firstName: '',
      lastName: '',
      designationGUID: 0,
      phone: '',
      mobile: '',
      emailID: '',
      locationGUID: 0,
      createdGUID: 0,
      modifiedGUID: 0,
      doctorGUID: 0,
      billingAddressInfoGUID: 0,
      isAdmin: false,
      isSuperAdmin: false,
      hasLPermission: false
    };
    const successMsg = isEditMode ? 'User updated successfully' : 'User added successfully';
    const response = await saveUser(payload, successMsg, '');
    if (response.success) {
      onClose?.();
      onRefresh?.();
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const successMsg = `User deleted successfully`;
      const response = await deleteUser(userData?.userGUID, successMsg, '');
      if (response && response.success) {
        onClose?.();
        onRefresh?.();
      }
    } catch (err) {
      console.log('Failed to delete Software Name:', err);
    } finally {
      setDeleting(false);
    }
  };

  const fetchDependencies = async () => {
    try {
      const [userRoles] = await Promise.all([fetchGlobalTypesCombo(MastersId.USER_ROLE)]);

      const loadedRoles = userRoles || [];

      setUserRoles(loadedRoles);
    } catch (err) {
      console.log('Failed to fetch dependencies:', err);
    }
  };

  // ── Has any visible error ──────────────────────────────────────────────────
  const hasErrors = Object.keys(validate(form, isEditMode)).length > 0;

  return (
    <Box component="form" noValidate autoComplete="off">
      {/* ── Mode label ──────────────────────────────────────────────────── */}

      <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2.5 }}>
        {/* Username */}
        <Box sx={{ flex: '1 1 50%', minWidth: '200px' }}>
          <TextField
            fullWidth
            label="Username"
            placeholder="e.g. john.doe"
            value={form.userName}
            onChange={handleChange('userName')}
            onBlur={handleBlur('userName')}
            error={touched.userName && Boolean(errors.userName)}
            helperText={touched.userName && errors.userName}
            size="small"
            sx={{
              ...FilterFieldSx,
              '& input': {
                textTransform: 'uppercase'
              }
            }}
            slotProps={{
              input: { readOnly: !isEditAllowed }
            }}
          />
        </Box>
        {!isEditMode && (
          <>
            <Box sx={{ flex: '1 1 50%', minWidth: '200px' }}>
              <TextField
                fullWidth
                label="Password"
                placeholder={isEditMode ? 'Leave blank to keep current' : 'Min. 6 characters'}
                value={form.password}
                onChange={handleChange('password')}
                onBlur={handleBlur('password')}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                size="small"
                slotProps={{
                  input: { readOnly: !isEditAllowed }
                }}
                sx={{
                  ...FilterFieldSx,
                  '& input': {
                    textTransform: 'uppercase'
                  }
                }}
              />
            </Box>

            <Box sx={{ flex: '1 1 50%', minWidth: '200px' }}>
              <TextField
                fullWidth
                label="Confirm Password"
                placeholder="Repeat password"
                value={form.confirmPassword}
                onChange={handleChange('confirmPassword')}
                onBlur={handleBlur('confirmPassword')}
                error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                helperText={touched.confirmPassword && errors.confirmPassword}
                size="small"
                slotProps={{
                  input: { readOnly: !isEditAllowed }
                }}
                sx={{
                  ...FilterFieldSx,
                  '& input': {
                    textTransform: 'uppercase'
                  }
                }}
              />
            </Box>
          </>
        )}

        {/* Role */}
        <Box sx={{ flex: '1 1 50%', minWidth: '200px' }}>
          <TextField
            select
            fullWidth
            label="Role"
            value={form.roleGUID || ''}
            onChange={handleChange('roleGUID')}
            onBlur={handleBlur('roleGUID')}
            error={touched.roleGUID && Boolean(errors.roleGUID)}
            helperText={touched.roleGUID && errors.roleGUID}
            size="small"
            slotProps={{
              input: { readOnly: !isEditAllowed }
            }}
            sx={FilterFieldSx}
          >
            <MenuItem value="" disabled>
              Select Role
            </MenuItem>
            {userRoles.map((role) => (
              <MenuItem key={role.globalTypeID} value={role.globalTypeID} sx={DropDownSX}>
                {role.globalTypeName}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* isActive */}
        <Box sx={{ flex: '1 1 50%', minWidth: '200px', display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.isActive}
                onChange={handleChange('isActive')}
                readOnly={!isEditAllowed}
                size="small"
                sx={{
                  color: 'primary.main',
                  '&.Mui-checked': { color: CSS_PRIMARY }
                }}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Active
              </Typography>
            }
          />
        </Box>
      </Box>

      {/* ── Actions ─────────────────────────────────────────────────────── */}
      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        {/* <Button
            variant="contained"
            size="small"
            startIcon={
              deleting ? <CircularProgress size={14} color="inherit" /> : <Trash size={15} />
            }
            disabled={deleting}
            onClick={handleDelete}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              background: '#e05757',
              '&:hover': { background: '#6d6464' },
              boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
              display: !isEditMode || !isDeleteAllowd ? 'none' : 'inline-flex'
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button> */}
        {/* Save */}
        <Button
          variant="contained"
          size="small"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={15} />}
          disabled={saving || (hasErrors && Object.keys(touched).length > 0)}
          onClick={handleSave}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            minWidth: 100,
            background: 'var(--primary, #36c95f)',
            '&:hover': { background: 'var(--primary-dark, #27ae60)' },
            boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
            display: !isEditAllowed ? 'none' : 'inline-flex'
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default AddUsers;
